// src/services/profileService.js
import { request, getImageUrl } from "./api";
import { uploadAvatarAsset } from "./assetUploadService";

/**
 * User Profile API (backend-ready)
 * - Uses request() wrapper (token + normalized errors)
 * - Normalizes "me" response shapes so UI won't show empty
 * - PATCH-first for partial updates (better UX + less payload)
 *   -> Falls back to PUT if backend doesn't support PATCH
 * - Supports optional avatar upload
 */

/** ----------------------------
 * Response Normalizers
 * ---------------------------- */
function normalizeMe(payload) {
  if (!payload) return null;

  let obj = null;
  if (payload?.user && typeof payload.user === "object") obj = payload.user;
  else if (payload?.data?.user && typeof payload.data.user === "object") obj = payload.data.user;
  else if (payload?.data && typeof payload.data === "object" && !Array.isArray(payload.data)) obj = payload.data;
  else if (typeof payload === "object" && !Array.isArray(payload)) obj = payload;

  if (!obj) return null;

  // make sure phoneNumber becomes phone for UI convenience
  if (obj.phoneNumber !== undefined && obj.phone === undefined) {
    obj = { ...obj, phone: obj.phoneNumber };
  }

  // make sure gender is a top‑level property (backend may return gender)
  if (obj.gender !== undefined) {
    obj = { ...obj, gender: obj.gender };
  }

  // Resolve avatarUrl to an absolute URL so it works in every environment.
  // The backend may store a root-relative path ("/images/avatars/...") or a
  // full URL. getImageUrl() handles all three shapes safely.
  const rawAvatar = obj.avatarUrl ?? obj.avatar_url ?? obj.avatar ?? null;
  if (rawAvatar !== undefined) {
    obj = { ...obj, avatarUrl: getImageUrl(rawAvatar) };
  }

  return obj;
}

function normalizeWrite(payload) {
  const me = normalizeMe(payload);
  return me || payload;
}

/** ----------------------------
 * Error helpers
 * ---------------------------- */
function looksLikeMethodNotAllowed(err) {
  const msg = String(err?.message || "");
  return (
    msg.includes("404") ||
    msg.includes("405") ||
    msg.toLowerCase().includes("not found") ||
    msg.toLowerCase().includes("method not allowed")
  );
}

/** ----------------------------
 * API
 * ---------------------------- */
export const userAPI = {
  me: async (options = {}) => {
    const res = await request({
      url: "/api/users/me",
      method: "GET",
      signal: options.signal,
    });
    return normalizeMe(res);
  },

  updateMe: async (payload, options = {}) => {
    if (!payload || typeof payload !== "object") {
      throw new Error("Request failed: Invalid profile payload");
    }

    // canonicalize the body to backend expectations
    const body = { ...payload };
    if (body.phone !== undefined) {
      body.phoneNumber = body.phone;
      delete body.phone;
    }
    if (body.birthdate !== undefined) {
      body.birthDate = body.birthdate;
      delete body.birthdate;
    }
    if (body.gender !== undefined) {
      // backend expects uppercase values
      body.gender = String(body.gender).toUpperCase();
    }

    try {
      const res = await request({
        url: "/api/users/me",
        method: "PATCH",
        data: body,
        signal: options.signal,
      });
      return normalizeWrite(res);
    } catch (err) {
      if (!looksLikeMethodNotAllowed(err)) throw err;

      const res = await request({
        url: "/api/users/me",
        method: "PUT",
        data: body,
        signal: options.signal,
      });
      return normalizeWrite(res);
    }
  },

  changePassword: (payload, options = {}) =>
    request({
      url: "/api/users/change-password",
      method: "POST",
      data: payload,
      signal: options.signal,
    }),

  /**
   * Upload avatar
   * Backend: Signed upload flow via /api/assets/uploads/init + /finalize.
   */
  uploadAvatar: async (file, options = {}) => {
    if (!file) throw new Error("Request failed: Missing file");
    const res = await uploadAvatarAsset(file, options);
    return normalizeWrite(res);
  },

  deleteMe: (options = {}) =>
    request({
      url: "/api/users/me",
      method: "DELETE",
      signal: options.signal,
    }),

  /**
   * Step-up auth: reveal unmasked PII (email, phone, birthdate).
   *
   * POST /api/users/me/reveal  { password }
   *
   * Returns { email, phone, birthdate } as a one-time response.
   * The server does NOT create a session — the client stores values in
   * memory and self-clears after 5 minutes.
   *
   * Rate-limited to 5 attempts per 15 minutes on the server.
   */
  reveal: async ({ password } = {}, options = {}) => {
    if (!password) throw new Error("Request failed: Password is required");
    const res = await request({
      url: "/api/users/me/reveal",
      method: "POST",
      data: { password },
      signal: options.signal,
    });
    // Unwrap data payload
    const d = res?.data ?? res;
    return {
      email:     d?.email     ?? null,
      phone:     d?.phone     ?? null,
      birthdate: d?.birthdate ?? null,
    };
  },
};
